
import convertMinimumRounding from "@/app/infrastructures/misc/common-library/ConvertMinimumRounding";
import { formatInputNoDecimal } from "@/app/infrastructures/misc/common-library/FormatInputNoDecimal";
import {
  commaDecimalToDecimal,
  formatByCountry,
  formatInputByCountry,
  convertDecimalAfterComma,
  numberOnly
} from "@/app/infrastructures/misc/Utils";
import { toNumber } from "@/app/infrastructures/misc/common-library/NormalizePayloadNumber";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CargoController } from "@/app/ui/controllers/CargoController";
import { DimensionPerKoliData } from "@/domain/entities/Cargo";
import { DimensionPerKoliDetail } from "@/domain/entities/Cargo";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import { cloneDeep } from "lodash";
import { prop, Vue } from "vue-class-component";
import { getConfigDimensionDivider } from "../../modules/cargo-module";

class Props {
  modelValue = prop<boolean>({ default: false, type: Boolean });
  isEdit = prop<boolean>({ default: false, type: Boolean });
}

export default class PopupDimensionPerKoli extends Vue.with(Props) {
  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  updated() {
    if (this.modelValue) {
      if (this.isEdit) {
        this.dimensionPerKoliList = cloneDeep(
          CargoController.dimensionPerKoliDataEdit.dimensionPieces
        );
      } else {
        this.dimensionPerKoliList = cloneDeep(
          CargoController.dimensionPerKoliData.dimensionPieces
        );
      }
      this.calculateTotalPieces();
    }
  }

  // current summary
  totalPieces = 0;
  calculateTotalPieces() {
    this.totalPieces = this.dimensionPerKoliList.length;
  }
  get errorTotalPieces() {
    if (Number(this.totalPieces) > 15) {
      return "Total koli melebihi batas maksimum 15 koli.";
    }
    return "";
  }
  get errorGrossWeight() {
    if (this.recalculateSummary.actualGrossWeight > 400)
      return "Total berat melebihi batas maksimum 400 kg.";
    return "";
  }
  get errorVolumeWeight() {
    if (this.recalculateSummary.actualVolumeWeight > 400)
      return "Total berat volume melebihi batas maksimum 400 kg.";
    return "";
  }
  get allErrorsSummary(): string[] {
    return [
      this.errorTotalPieces,
      this.errorGrossWeight,
      this.errorVolumeWeight
    ].filter((e: string) => e);
  }

  // list koli
  currentDimensionPerKoliList = new DimensionPerKoliData();
  dimensionPerKoliList: DimensionPerKoliDetail[] = [];
  get recalculateSummary(): DimensionPerKoliData {
    return new DimensionPerKoliData({
      totalPieces: this.dimensionPerKoliList.length,
      actualGrossWeight: this.dimensionPerKoliList.reduce(
        (total, item) =>
          commaDecimalToDecimal(
            convertDecimalAfterComma(
              total +
                (item.sttPieceGrossWeight
                  ? commaDecimalToDecimal(item.sttPieceGrossWeight)
                  : 0),
              2
            )
          ),
        0
      ),
      actualVolumeWeight: this.dimensionPerKoliList.reduce(
        (total, item) =>
          commaDecimalToDecimal(
            convertDecimalAfterComma(
              total +
                (item.sttPieceVolumeWeight
                  ? commaDecimalToDecimal(item.sttPieceVolumeWeight)
                  : 0),
              2
            )
          ),
        0
      )
    });
  }
  onInputGrossWeight(value: any, index: number) {
    const newValue: any = this.formatNumberToString(value);
    this.dimensionPerKoliList[index].sttPieceGrossWeight = newValue;
  }
  onInputDimension({ length, width, height }: any, index: number) {
    const formatLength: any =
      length !== "" ? commaDecimalToDecimal(length) : "";
    const formatWidth: any = width !== "" ? commaDecimalToDecimal(width) : "";
    const formatHeight: any =
      height !== "" ? commaDecimalToDecimal(height) : "";

    this.dimensionPerKoliList[index].sttPieceLength = formatLength;
    this.dimensionPerKoliList[index].sttPieceWidth = formatWidth;
    this.dimensionPerKoliList[index].sttPieceHeight = formatHeight;

    const total = this.calculateVolumeWeight({
      length: formatLength,
      width: formatWidth,
      height: formatHeight
    });

    this.dimensionPerKoliList[index].sttPieceVolumeWeight = total;
  }
  calculateVolumeWeight({ length, width, height }: any) {
    return convertMinimumRounding(
      commaDecimalToDecimal(
          (length * width * height) / this.dimensionDivider
      ), 1
    );
  }
  get dimensionDivider(): number {
    return Number(getConfigDimensionDivider("plane"));
  }

  // validation
  get disabled(): boolean {
    const findEmpty: boolean = this.dimensionPerKoliList.some(
      (e: DimensionPerKoliDetail) =>
        !(commaDecimalToDecimal(e.sttPieceGrossWeight) >= 0.1) ||
        !e.sttPieceLength ||
        !e.sttPieceWidth ||
        !e.sttPieceHeight
    );

    const { totalPieces } = this.recalculateSummary;

    const errorWithoutPieces = !!this.allErrorsSummary.filter(
      (e: string) => !e.includes("koli")
    ).length;

    return !totalPieces || errorWithoutPieces || findEmpty;
  }

  // utils
  formatFloat(value: number) {
    return formatByCountry(value, { type: "weight", country: this.countryCode });
  }
  formatNumberToString(value: string | number) {
    const strValue = String(value).trim();
    if (strValue === "") {
      return "";
    }
    const normalized = toNumber(strValue);
    if (normalized < 0.1 && strValue.includes("0")) {
      const sep = this.countryCode === "MY" ? "." : ",";
      return `0${sep}1`;
    }
    return formatInputByCountry(strValue, { type: "weight", country: this.countryCode });
  }
  formatNumberNoDecimalToString(value: string | number) {
    const strValue = String(value).trim();
    const normalized = commaDecimalToDecimal(strValue)
    if (normalized < 1) {
      return "1";
    }

    if (strValue === "") {
      return "";
    }

    const _numberOnly = numberOnly(strValue)
    const noDecimal = formatInputNoDecimal(String(_numberOnly));

    return noDecimal === "" ? "1" : noDecimal;
  }

  formatValueToNumber(value: string): number {
    return value ? Number(commaDecimalToDecimal(value)) : 0;
  }
  formatMoney(value: number): string {
    return formatByCountry(value, { type: "quantity", country: this.countryCode });
  }

  onClose() {
    this.$emit("update:modelValue", false);
  }
  onSave() {
    CargoController.setIsDimensionSaved(true);
    CargoController.setIsSttUpdated(false);

    if (this.isEdit) {
      CargoController.setDimensionPerKoliDataEdit(
        new DimensionPerKoliData({
          totalPieces: this.totalPieces,
          dimensionPieces: this.dimensionPerKoliList.map(
            (item: DimensionPerKoliDetail) =>
              new DimensionPerKoliDetail({
                ...item,
                sttPieceGrossWeight: Number(
                  item.sttPieceGrossWeight
                    ? commaDecimalToDecimal(item.sttPieceGrossWeight)
                    : 0
                ),
                sttPieceVolumeWeight: Number(item.sttPieceVolumeWeight),
                sttPieceLength: Number(item.sttPieceLength),
                sttPieceWidth: Number(item.sttPieceWidth),
                sttPieceHeight: Number(item.sttPieceHeight)
              })
          )
        })
      );
    } else {
      CargoController.setDimensionPerKoliData(
        new DimensionPerKoliData({
          dimensionPieces: this.dimensionPerKoliList.map(
            (item: DimensionPerKoliDetail) =>
              new DimensionPerKoliDetail({
                ...item,
                sttPieceGrossWeight: Number(
                  item.sttPieceGrossWeight
                    ? commaDecimalToDecimal(item.sttPieceGrossWeight)
                    : 0
                ),
                sttPieceVolumeWeight: Number(item.sttPieceVolumeWeight),
                sttPieceLength: Number(item.sttPieceLength),
                sttPieceWidth: Number(item.sttPieceWidth),
                sttPieceHeight: Number(item.sttPieceHeight)
              })
          )
        })
      );
    }
    this.$emit("update:modelValue", false);
  }
}
